<template>
  <div class="photo-picker">
    <div v-if="modelValue" class="photo-preview-container">
      <div class="photo-preview">
        <img :src="modelValue" :alt="t('Foto')" />
        <div class="photo-overlay">
          <Button
            icon="pi pi-times"
            rounded
            text
            severity="danger"
            size="small"
            :data-testid="`${testIdPrefix}-remove`"
            @click="clearPhoto"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="photo-upload-area"
      :class="{ uploading: isUploading }"
      @click="triggerFileInput"
    >
      <i v-if="isUploading" class="pi pi-spin pi-spinner upload-icon"></i>
      <i v-else class="pi pi-cloud-upload upload-icon"></i>
      <span>{{ isUploading ? t('Enviando...') : t('Clique para enviar') }}</span>

      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
        style="display: none;"
        :data-testid="`${testIdPrefix}-file`"
        @change="handleFileSelect"
      />
    </div>

    <small v-if="errorMessage" class="error-message" :data-testid="`${testIdPrefix}-error`">{{ errorMessage }}</small>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAcoesMarketing } from '../../composables/useAcoesMarketing';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';

// Same picker as SiteUnidadesPhotoPicker, but uploading through
// /marketing/acoes/upload_foto (Full/Franqueado/Gerente) — the site-unidades
// endpoint is Full-only, so the component cannot be shared as-is.
const { t } = useI18n();
const { uploadFoto } = useAcoesMarketing();

const props = withDefaults(defineProps<{
  modelValue?: string;
  testIdPrefix?: string;
}>(), {
  modelValue: '',
  testIdPrefix: 'acoes-marketing-photo'
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isUploading = ref<boolean>(false);
const errorMessage = ref<string>('');

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];

const triggerFileInput = (): void => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const clearPhoto = (): void => {
  errorMessage.value = '';
  emit('update:modelValue', '');
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const handleFileSelect = async (event: Event): Promise<void> => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  errorMessage.value = '';

  if (!ALLOWED_TYPES.includes(file.type)) {
    errorMessage.value = t('Tipo de arquivo não permitido. Use JPG, PNG, GIF, WEBP ou HEIC.');
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  if (file.size > MAX_SIZE && file.type !== 'image/heic') {
    errorMessage.value = t('Arquivo muito grande. Máximo permitido: 5MB');
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  isUploading.value = true;
  try {
    const url = await uploadFoto(file);
    emit('update:modelValue', url);
    log('AcoesMarketingPhotoPicker', 'handleFileSelect', 'Photo uploaded', { name: file.name, url });
  } catch (err) {
    const error = err as Error;
    logError('AcoesMarketingPhotoPicker', 'handleFileSelect', 'Upload failed', error);
    errorMessage.value = error.message || t('Erro ao enviar a foto');
    eventBus.emit('globalMessage.error', t('Erro ao enviar a foto') + ': ' + error.message);
  } finally {
    isUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};
</script>

<style scoped>
.photo-picker { display: flex; flex-direction: column; gap: 0.5rem; }
.photo-preview-container { width: fit-content; }
.photo-preview { position: relative; width: 120px; height: 120px; border-radius: 8px; overflow: hidden; border: 1px solid var(--p-surface-300, #d1d5db); }
.photo-preview img { width: 100%; height: 100%; object-fit: cover; }
.photo-overlay { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s; }
.photo-preview:hover .photo-overlay { opacity: 1; }
.photo-upload-area { width: 120px; height: 120px; border: 2px dashed #d1d5db; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; cursor: pointer; text-align: center; padding: 8px; font-size: 0.75rem; color: #666; }
.photo-upload-area:hover { border-color: #6366f1; }
.photo-upload-area.uploading { cursor: wait; }
.upload-icon { font-size: 1.5rem; color: #6366f1; }
.error-message { color: var(--p-red-500); font-size: 0.8125rem; }
</style>
